import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { CURRENCIES } from "@/lib/countryData";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format a number to max 2 decimal places, removing trailing zeros
 * e.g., 22.243 → 22.24, 22.00 → 22, 22.50 → 22.5
 */
export function formatNumber(value: number | null | undefined, maxDecimals: number = 2): string {
  if (value === null || value === undefined) return '-';
  
  // Round to specified decimal places
  const rounded = Number(value.toFixed(maxDecimals));
  
  // Convert to string, which automatically removes trailing zeros
  return rounded.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
  });
}

/**
 * Format a number keeping exactly `decimals` digits, so figures in a column
 * line up instead of ragged-edging: 20 → "20.0", 9.5 → "9.5".
 */
export function formatFixed(value: number | null | undefined, decimals: number = 1): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '-';
  return value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}


/**
 * Format weight - displays in kg if >= 1000g, otherwise in grams
 * e.g., 500 → "500 g", 1500 → "1.50 kg", 2000 → "2.00 kg"
 */
export function formatWeight(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-';
  
  if (value >= 1000) {
    const kg = value / 1000;
    const formatted = kg.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${formatted} kg`;
  }
  
  return `${Math.round(value).toLocaleString('en-US')} g`;
}

/**
 * Format currency value - always shows 2 decimal places
 * e.g., 22 → 22.00, 22.5 → 22.50, 22.243 → 22.24
 */
export function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-';
  
  return Math.round(value).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

/**
 * Format price per gram - always shows 3 decimal places
 * e.g., 0.13 → 0.130, 0.134 → 0.134, 1.5 → 1.500
 */
export function formatPricePerGram(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-';
  
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
}

/**
 * Calculate and format total price (price per gram × quantity)
 * Returns null if either value is missing
 */
export function calculateTotalPrice(
  pricePerUnit: number | null | undefined,
  quantityGrams: number | null | undefined
): number | null {
  if (pricePerUnit === null || pricePerUnit === undefined) return null;
  if (quantityGrams === null || quantityGrams === undefined) return null;
  return pricePerUnit * quantityGrams;
}
/**
 * Get currency code prefix for display (e.g., "USD ", "CAD ", "EUR ").
 * Returns the ISO code + space for any currency in CURRENCIES; falls back to "USD ".
 */
export function getCurrencySymbol(currency: string | null | undefined): string {
  if (currency && (CURRENCIES as readonly string[]).includes(currency)) {
    return `${currency} `;
  }
  return 'USD ';
}

/**
 * Format a date-only string (YYYY-MM-DD) for display without timezone conversion.
 * This prevents the off-by-one-day issue caused by JavaScript parsing date strings as UTC.
 * e.g., "2025-06-19" → "6/19/2025"
 */
/**
 * Number of days a listing is considered "new" after creation.
 * Change this single value to adjust the threshold everywhere.
 */
export const NEW_LISTING_THRESHOLD_DAYS = 2;

/**
 * Returns true if the listing was created within the last NEW_LISTING_THRESHOLD_DAYS days.
 */
export function isNewListing(createdAt: string | null | undefined): boolean {
  if (!createdAt) return false;
  const created = new Date(createdAt);
  const threshold = new Date();
  threshold.setDate(threshold.getDate() - NEW_LISTING_THRESHOLD_DAYS);
  return created >= threshold;
}

export function formatDateString(dateString: string | null | undefined): string {
  if (!dateString) return '—';
  
  // Split the date string to avoid timezone conversion
  const parts = dateString.split('-');
  if (parts.length !== 3) return dateString;
  
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const day = parseInt(parts[2], 10);
  
  // Return formatted as M/D/YYYY
  return `${month}/${day}/${year}`;
}

/**
 * Levenshtein edit distance between two strings.
 * Useful for fuzzy matching small option lists with typo tolerance.
 */
export function levenshtein(a: string, b: string): number {
  const m = a.length, n = b.length;
  const dp: number[] = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    let prev = i - 1;
    dp[0] = i;
    for (let j = 1; j <= n; j++) {
      const temp = dp[j];
      dp[j] = a[i - 1] === b[j - 1] ? prev : 1 + Math.min(prev, dp[j], dp[j - 1]);
      prev = temp;
    }
  }
  return dp[n];
}
